import axios from 'axios';
import { LoginRequest, LoginWithOTPRequest, SignupRequest, TokenResponse, User } from '../types/auth';
import { Invite, CreateInviteRequest, InviteResponse } from '../types/invite';
import { PaymentFormData, PaymentResponse, PendingSale, ApprovalRequest, RejectRequest, UnifiedTransaction, TransactionComment, TransactionCommentsResponse, TransactionDocument, TransactionDocumentsResponse, DocumentCountResponse, PaymentLinkData, PaymentLinkListResponse, PaymentLinkCustomerView, CustomerPayResponse, CreatePaymentLinkData, PendingPaymentLinkListResponse, PendingSelfAuthListResponse, TFN, TFNListResponse } from '../types/payment';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Configure axios to treat 2xx responses as success
  validateStatus: function (status) {
    return status >= 200 && status < 300; // Default behavior
  },
});

export class AuthAPI {
  static setAuthToken(token: string) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  static removeAuthToken() {
    delete api.defaults.headers.common['Authorization'];
  }

  static async login(credentials: LoginRequest): Promise<TokenResponse> {
    try {
      console.log('AuthAPI: Making login request to /auth/login');
      const response = await api.post('/auth/login', credentials);
      
      console.log('AuthAPI: Response status:', response.status);
      console.log('AuthAPI: Response data:', response.data);
      
      // Check for 2FA required (HTTP 202)
      if (response.status === 202) {
        console.log('AuthAPI: 2FA required (status 202)');
        throw new Error('2FA_REQUIRED');
      }
      
      // Check if response has the expected token structure
      if (!response.data.access_token) {
        console.log('AuthAPI: No access_token in response, treating as error');
        throw new Error(response.data.detail || 'Invalid login response');
      }
      
      return response.data;
    } catch (error: any) {
      console.log('AuthAPI: Error caught:', error.response?.status, error.response?.data);
      if (error.response?.status === 202) {
        console.log('AuthAPI: 2FA required (error status 202)');
        throw new Error('2FA_REQUIRED');
      }
      
      // If it's our custom error, re-throw it
      if (error.message === '2FA_REQUIRED') {
        console.log('AuthAPI: Re-throwing 2FA_REQUIRED error');
        throw error;
      }
      
      const errorMessage = error.response?.data?.detail || error.message || 'Login failed';
      console.log('AuthAPI: Throwing error:', errorMessage);
      throw new Error(errorMessage);
    }
  }

  static async loginWithOTP(credentials: LoginWithOTPRequest): Promise<TokenResponse> {
    try {
      const response = await api.post('/auth/login/otp', credentials);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'OTP login failed');
    }
  }

  static async signup(data: SignupRequest): Promise<User> {
    try {
      const response = await api.post('/auth/signup', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Signup failed');
    }
  }

  // New method specifically for invite-based signup
  static async signupWithInvite(data: Omit<SignupRequest, 'email'> & { invite_token: string }): Promise<User> {
    try {
      // The backend will extract email from the invite token
      const response = await api.post('/auth/signup', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Signup failed');
    }
  }

  static async getMe(): Promise<User> {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get user info');
    }
  }

  static async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error: any) {
      // Even if logout fails on backend, we should clear local state
      console.warn('Logout request failed:', error.response?.data?.detail);
    }
  }

  static async refreshToken(refreshToken: string): Promise<{ access_token: string }> {
    try {
      const response = await api.post('/auth/refresh', {
        refresh_token: refreshToken,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Token refresh failed');
    }
  }

  static async heartbeat(wasActive: boolean = true): Promise<{ status: string; message: string }> {
    try {
      const response = await api.post('/auth/heartbeat', { was_active: wasActive });
      return response.data;
    } catch (error: any) {
      // Rethrow with the specific error detail for handling
      const errorDetail = error.response?.data?.detail;
      if (errorDetail === 'SESSION_INACTIVE_LOGOUT') {
        throw new Error('SESSION_INACTIVE_LOGOUT');
      }
      if (errorDetail === 'USER_DEACTIVATED') {
        throw new Error('USER_DEACTIVATED');
      }
      throw new Error(errorDetail || 'Heartbeat failed');
    }
  }

  // Password management endpoints
  static async forgotPassword(email: string): Promise<{ message: string }> {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Password reset request failed');
    }
  }

  static async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    try {
      const response = await api.post('/auth/reset-password', {
        token,
        new_password: newPassword
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Password reset failed');
    }
  }

  static async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    try {
      const response = await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Password change failed');
    }
  }

  // Admin endpoints
  static async adminResetUserPassword(userId: string): Promise<{ message: string }> {
    try {
      const response = await api.post('/auth/admin/reset-user-password', {
        user_id: userId
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Admin password reset failed');
    }
  }

  static async getPasswordResettableRoles(): Promise<any> {
    try {
      const response = await api.get('/auth/admin/password-resettable-roles');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get resettable roles');
    }
  }

  static async getUserPasswordHistory(userId: string): Promise<any> {
    try {
      const response = await api.get(`/auth/admin/password-history/${userId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get password history');
    }
  }

  static async deleteUser(userId: string): Promise<{ message: string }> {
    try {
      const response = await api.delete(`/auth/users/${userId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to delete user');
    }
  }

  // 2FA endpoints
  static async setup2FA(): Promise<{ message: string; email: string }> {
    try {
      const response = await api.post('/auth/2fa/setup');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || '2FA setup failed');
    }
  }

  static async enable2FA(otpCode: string): Promise<{ message: string }> {
    try {
      const response = await api.post('/auth/2fa/enable', {
        otp_code: otpCode
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || '2FA enable failed');
    }
  }

  static async validateInviteToken(token: string): Promise<{ email: string; role: string; expires_at: string; is_used: boolean }> {
    try {
      const response = await api.get(`/auth/invites/validate/${token}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to validate invite token');
    }
  }
}

export class InviteAPI {
  static async createInvite(data: CreateInviteRequest): Promise<Invite> {
    try {
      const response = await api.post('/auth/invites', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to create invite');
    }
  }

  static async getInvites(offset: number = 0, limit: number = 50): Promise<Invite[]> {
    try {
      const response = await api.get('/auth/invites', {
        params: { offset, limit }
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch invites');
    }
  }

  static async getAvailableRoles(): Promise<InviteResponse> {
    try {
      const response = await api.get('/auth/invites/available-roles');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch available roles');
    }
  }

  static async deleteInvite(inviteId: string): Promise<{ message: string }> {
    try {
      const response = await api.delete(`/auth/invites/${inviteId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to delete invite');
    }
  }
}

// Payment API class
export class PaymentAPI {
  // Payment operations
  static async createSale(data: PaymentFormData): Promise<PaymentResponse> {
    try {
      // Map sale_type and sale_state to extra_category and extra_state for backend compatibility
      const mappedData = {
        ...data,
        extra_category: data.sale_type,
        extra_state: data.sale_state,
        // Include domain fields directly in the payment request
        sale_domains: data.sale_domains,
        utility_providers: data.utility_providers,
        sale_notes: data.sale_notes,
      };
      delete mappedData.sale_type;
      delete mappedData.sale_state;
      
      // All sales now go through /payments/sale which handles both payment processing and domain storage
      const response = await api.post('/payments/sale', mappedData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Payment failed');
    }
  }


  static async createSale3RI(data: PaymentFormData): Promise<PaymentResponse> {
    try {
      const mappedData = {
        ...data,
        extra_category: data.sale_type,
        extra_state: data.sale_state,
        sale_domains: data.sale_domains,
        utility_providers: data.utility_providers,
        sale_notes: data.sale_notes,
      };
      delete mappedData.sale_type;
      delete mappedData.sale_state;

      const response = await api.post('/payments/sale-3ri', mappedData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Self Authorization payment failed');
    }
  }

  static async authorize(data: PaymentFormData): Promise<PaymentResponse> {
    try {
      // Map sale_type and sale_state to extra_category and extra_state for backend compatibility
      const mappedData = {
        ...data,
        extra_category: data.sale_type,
        extra_state: data.sale_state,
      };
      delete mappedData.sale_type;
      delete mappedData.sale_state;
      
      // processor_id is passed as-is to backend
      
      const response = await api.post('/payments/authorize', mappedData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Authorization failed');
    }
  }

  static async validate(data: PaymentFormData): Promise<PaymentResponse> {
    try {
      // Map sale_type and sale_state to extra_category and extra_state for backend compatibility
      const mappedData = {
        ...data,
        extra_category: data.sale_type,
        extra_state: data.sale_state,
      };
      delete mappedData.sale_type;
      delete mappedData.sale_state;
      
      // processor_id is passed as-is to backend
      
      const response = await api.post('/payments/validate', mappedData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Validation failed');
    }
  }


  static async capture(transactionid: string, amount?: number, processor_key?: string): Promise<PaymentResponse> {
    try {
      const response = await api.post('/payments/capture', { transactionid, amount, processor_key });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Capture failed');
    }
  }

  static async void(transactionid: string, processor_key?: string): Promise<PaymentResponse> {
    try {
      const response = await api.post('/payments/void', { transactionid, processor_key });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Void failed');
    }
  }

  static async refund(transactionid: string, amount?: number, processor_key?: string): Promise<PaymentResponse> {
    try {
      const response = await api.post('/payments/refund', { transactionid, amount, processor_key });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Refund failed');
    }
  }
}

// Processing section (Bill sales, processor pages) — company/processor-side detail.
export interface ProcessingProvider {
  domain: string;
  provider_id?: string | null;
  name?: string | null;
}

// One service's utility account details (provider_accounts[service]).
export interface ProviderAccount {
  account_number?: string | null;
  provider_phone?: string | null;
  provider_account_status?: string | null;
  provider_username?: string | null;
  provider_password?: string | null;
  provider_pin?: string | null;
  guest_pay_link?: string | null;
  autopay_enrolled?: boolean | null;
}

export interface ProcessingInfo {
  pending_sale_id: string;
  category?: string | null;
  // Read-only reference (mirrored from the sale)
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  company?: string | null;
  date_of_birth?: string | null;
  phone?: string | null;
  email?: string | null;
  amount?: number | null;
  sales_tax?: number | null;
  tax_exempt?: boolean | null;
  account_number?: string | null;
  provider_name?: string | null;
  provider_phone?: string | null;
  ssn_last4?: string | null;
  has_ssn?: boolean | null;
  dl_last4?: string | null;
  drivers_license_state?: string | null;
  dl_issuing_date?: string | null;
  dl_expiration_date?: string | null;
  has_dl?: boolean | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  shipping_firstname?: string | null;
  shipping_lastname?: string | null;
  shipping_company?: string | null;
  shipping_country?: string | null;
  shipping_address_1?: string | null;
  shipping_address_2?: string | null;
  shipping_city?: string | null;
  shipping_state?: string | null;
  shipping_zip?: string | null;
  sale_domains?: string[] | null;
  providers?: ProcessingProvider[] | null;
  created_by_name?: string | null;   // Agent (sales side)
  processed_by_name?: string | null; // assigned processor
  processor_sale_status?: string | null;
  // Editable processor fields
  id_type?: string | null;
  passport_last4?: string | null;
  has_passport?: boolean | null;
  // Per-service account details, keyed by service (Electricity / Gas / Water)
  provider_accounts?: Record<string, ProviderAccount> | null;
  provider_account_status?: string | null;
  provider_username?: string | null;
  provider_password?: string | null;
  provider_pin?: string | null;
  guest_pay_link?: string | null;
  autopay_enrolled?: boolean | null;
  total_bill_amount?: number | null;
  past_due_amount?: number | null;
  overdue_balance?: number | null;
  next_bill_date?: string | null;
  payment_method?: string | null;
  utility_payment_status?: string | null;
  amount_collected?: number | null;
  confirmation_number?: string | null;
  paid_at?: string | null;
  reversal_date?: string | null;
  appointment_date?: string | null;    // Restore / New start / Transfer date (required for those emails)
  appointment_time?: string | null;    // "All day (8:00 AM - 8:00 PM)" or "H:MM AM - H:MM PM"
  cancellation_date?: string | null;   // Cancel / Bill + Cancel / Disconnect (required)
  // New sales: separate deposit confirmation (once only)
  deposit_informed_at?: string | null;
  deposit_informed_by_name?: string | null;
  deposit_informed_via?: string | null;
  deposit_informed_provider?: string | null;
  // Template Inform Customer sends: bill|restore|cancellation|new_service|transfer (null = standard invoice)
  inform_kind?: string | null;
  // Testing switch: Inform Customer / deposit buttons stay available after a send
  allow_resend?: boolean;
  // Server switches: each button is hidden while its switch is off (live: off)
  inform_enabled?: boolean;
  deposit_enabled?: boolean;
  agent_call_flag?: boolean | null;
  // Inform Customer (provider-branded invoice, once only)
  customer_informed_at?: string | null;
  customer_informed_by_name?: string | null;
  customer_informed_via?: string | null;
  customer_informed_provider?: string | null;
  can_reveal: boolean;
  can_edit?: boolean;
  // Pseudo identity (processing team only; null for others) + who may edit it
  // (TL and above, or the assigned processor agent; same rule for Customer identity)
  pseudo_identity?: PseudoIdentity | null;
  can_edit_pseudo?: boolean;
  can_edit_identity?: boolean;
  // Deposit toggle (assigned processor agent or TL and above)
  is_deposit?: boolean;
  deposit_marked_at?: string | null;
  deposit_marked_by_name?: string | null;
  can_toggle_deposit?: boolean;
}

// Payment History filter dropdown choices over the whole dataset (GET /transactions/filter-options)
export interface TransactionFilterOptions {
  created_by?: { id: string; name: string }[];
  tx_types?: string[];
  card_brands?: string[];
  response_codes?: string[];
  tfns?: string[];
  voided_refunded_by?: { id: string; name: string }[];
}

// Alternate identity for the sale (same fields as the customer identity), empty
// until the processing team fills it in. SSN / DL # / passport as has_* + *_last4 only.
export interface PseudoIdentity {
  [field: string]: string | boolean | null | undefined;
  sales_tax?: string | null;
  tax_exempt?: boolean | null;
  has_ssn?: boolean;
  ssn_last4?: string | null;
  has_dl?: boolean;
  dl_last4?: string | null;
  has_passport?: boolean;
  passport_last4?: string | null;
}

export interface ProcessingInfoUpdate {
  id_type?: string | null;
  passport_number?: string | null;
  account_number?: string | null;
  provider_name?: string | null;
  provider_phone?: string | null;
  sale_domains?: string[] | null;
  utility_providers?: Record<string, string> | null;
  provider_account_status?: string | null;
  provider_username?: string | null;
  provider_password?: string | null;
  provider_pin?: string | null;
  guest_pay_link?: string | null;
  autopay_enrolled?: boolean | null;
  // Per-service account details; the server mirrors the first service into the single fields
  provider_accounts?: Record<string, ProviderAccount> | null;
  // Changed pseudo-identity keys only (raw ssn / drivers_license_number / passport_number)
  pseudo_identity?: Record<string, string | boolean | null>;
  total_bill_amount?: number | null;
  past_due_amount?: number | null;
  overdue_balance?: number | null;
  next_bill_date?: string | null;
  payment_method?: string | null;
  utility_payment_status?: string | null;
  amount_collected?: number | null;
  confirmation_number?: string | null;
  paid_at?: string | null;
  reversal_date?: string | null;
  appointment_date?: string | null;
  appointment_time?: string | null;
  cancellation_date?: string | null;
  agent_call_flag?: boolean | null;
  processor_sale_status?: string | null;
}

// Dropdown option lists (kept in sync with the backend validators)
export const PROCESSING_ID_TYPES = ['SSN', 'Passport', 'DL'] as const;
export const PROCESSING_ACCOUNT_STATUSES = ['Active', 'Suspended', 'Cancelled', 'Replaced'] as const;
export const PROCESSING_PAYMENT_METHODS = [
  'ACH', 'Debit card', 'Credit card', 'Check', 'Cash',
  'Money order', 'Gift card', 'Prepaid card', 'Bank/wire transfer', 'Digital wallet',
] as const;
export const PROCESSING_UTILITY_PAYMENT_STATUSES = ['Paid', 'No need to pay', 'Payment pending', 'Chargeback'] as const;
// Processor "Sale Status" outcome (written into processing_status)
export const PROCESSING_SALE_STATUSES: { value: string; label: string }[] = [
  { value: 'PENDING_ASSIGNMENT', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'UNDER_REVIEW', label: 'Under Review' },
];

// Approval API class
export class ApprovalAPI {
  static async getPendingSales(offset = 0, limit = 50): Promise<PendingSale[]> {
    try {
      const response = await api.get(`/approvals/pending?offset=${offset}&limit=${limit}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get pending sales');
    }
  }

  // Server-side paginated + filtered Pending / Rejected lists. Returns the page rows
  // plus full-dataset totals (count + amount) computed with the SAME filters.
  private static async pagedPendingList(path: string, params: {
    offset?: number; limit?: number; sortField?: string; sortDir?: string;
    filters?: Record<string, string>;
  }): Promise<{ items: PendingSale[]; total: number; total_amount: number }> {
    try {
      const qp = new URLSearchParams();
      qp.set('offset', String(params.offset ?? 0));
      qp.set('limit', String(params.limit ?? 20));
      if (params.sortField) qp.set('sort_field', params.sortField);
      if (params.sortDir) qp.set('sort_dir', params.sortDir);
      Object.entries(params.filters || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && String(v).trim() !== '') qp.set(k, String(v));
      });
      const response = await api.get(`${path}?${qp.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get transactions');
    }
  }

  static getPendingSalesPaged(params: {
    offset?: number; limit?: number; sortField?: string; sortDir?: string; filters?: Record<string, string>;
  }) {
    return this.pagedPendingList('/approvals/pending/paged', params);
  }

  static getRejectedTransactionsPaged(params: {
    offset?: number; limit?: number; sortField?: string; sortDir?: string; filters?: Record<string, string>;
  }) {
    return this.pagedPendingList('/approvals/rejected/paged', params);
  }

  static getFailedTransactionsPaged(params: {
    offset?: number; limit?: number; sortField?: string; sortDir?: string; filters?: Record<string, string>;
  }) {
    return this.pagedPendingList('/approvals/failed/paged', params);
  }

  // The current user's own sales still in the processing pipeline (read-only).
  static getMyPendingProcessingPaged(params: {
    offset?: number; limit?: number; sortField?: string; sortDir?: string; filters?: Record<string, string>;
  }) {
    return this.pagedPendingList('/sales/my/processing/pending/paged', params);
  }

  // The current user's own sales that have completed processing (read-only).
  static getMyProcessedPaged(params: {
    offset?: number; limit?: number; sortField?: string; sortDir?: string; filters?: Record<string, string>;
  }) {
    return this.pagedPendingList('/sales/my/processing/processed/paged', params);
  }

  // The current user's own terminal-outcome sales: cancelled, chargeback, voided
  // or refunded (read-only).
  static getMyTerminalSalesPaged(params: {
    offset?: number; limit?: number; sortField?: string; sortDir?: string; filters?: Record<string, string>;
  }) {
    return this.pagedPendingList('/sales/my/processing/terminal/paged', params);
  }

  // ---- Processing section (Bill sales, processor pages) -------------------
  // Full processor-side detail for a Bill sale. `saleId` may be a Transaction.id
  // or PendingSale.id; the backend resolves either.
  static async getProcessingInfo(saleId: string): Promise<ProcessingInfo> {
    const response = await api.get(`/sales/${saleId}/processing-info`);
    return response.data;
  }

  // Deposit toggle (top of the processing panel)
  static async setDeposit(saleId: string, isDeposit: boolean): Promise<ProcessingInfo> {
    try {
      const response = await api.put(`/sales/${saleId}/deposit`, { is_deposit: isDeposit });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to update Deposit');
    }
  }

  // Patch the processor fields. Only provided keys are updated. Sending an empty
  // string for a sensitive field clears it; omitting leaves it unchanged.
  static async updateProcessingInfo(saleId: string, body: ProcessingInfoUpdate): Promise<ProcessingInfo> {
    const response = await api.patch(`/sales/${saleId}/processing-info`, body);
    return response.data;
  }

  // "Inform Customer": send the provider-branded payment confirmation (once only).
  // providerId is required only when the sale has more than one provider.
  // channels: what the processor ticked ('email' / 'sms'); only those are used.
  static async informCustomer(saleId: string, providerId?: string, channels?: ('email' | 'sms')[]): Promise<ProcessingInfo> {
    try {
      const response = await api.post(`/sales/${saleId}/inform-customer`, { provider_id: providerId || null, channels });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to inform the customer');
    }
  }

  // New sales only: "security deposit received" email + SMS (includes the document-upload link). Once only.
  static async sendDepositConfirmation(saleId: string, providerId?: string, channels?: ('email' | 'sms')[]): Promise<ProcessingInfo> {
    try {
      const response = await api.post(`/sales/${saleId}/deposit-confirmation`, { provider_id: providerId || null, channels });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to send the deposit confirmation');
    }
  }

  // Reveal one decrypted sensitive value (TL and above). field: ssn|passport|password|pin
  static async revealProcessingField(saleId: string, field: 'ssn' | 'passport' | 'password' | 'pin' | 'pseudo_ssn' | 'pseudo_dl' | 'pseudo_passport'): Promise<string | null> {
    const response = await api.post(`/sales/${saleId}/processing-info/reveal`, null, { params: { field } });
    return response.data?.value ?? null;
  }

  // Self Authorization (3RI) sales awaiting approval — the 'no charge before
  // approval' queue shown in the unified approvals tab.
  static async getPendingSelfAuth(skip = 0, limit = 50): Promise<PendingSelfAuthListResponse> {
    try {
      const response = await api.get('/approvals/pending-self-auth', { params: { skip, limit } });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get pending self-authorizations');
    }
  }

  static async approveSale(pendingId: string, data: ApprovalRequest): Promise<PaymentResponse> {
    try {
      const response = await api.post(`/approvals/${pendingId}/approve`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Approval failed');
    }
  }

  static async rejectSale(pendingId: string, data: RejectRequest): Promise<{ ok: boolean }> {
    try {
      const response = await api.post(`/approvals/${pendingId}/reject`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Rejection failed');
    }
  }

  static async getPendingSaleEncryptedData(pendingId: string, decrypt = false, reason?: string): Promise<any> {
    try {
      const response = await api.get(`/approvals/${pendingId}/encrypted-data?decrypt=${decrypt}&reason=${reason || ''}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get encrypted data');
    }
  }

  // Decrypt card data stored on the Transaction row (standalone failed sales/captures that
  // have no PendingSale). Returns the same { decrypted_card_data, encrypted_card_available }
  // shape as getPendingSaleEncryptedData so the details panel can consume it interchangeably.
  static async getTransactionEncryptedData(txId: string, decrypt = false, reason?: string): Promise<any> {
    try {
      const response = await api.get(`/secure-cards/transaction/${txId}?decrypt=${decrypt}&reason=${encodeURIComponent(reason || '')}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get encrypted data');
    }
  }

}

// Transaction API class
export class TransactionAPI {
  static async getTransactions(offset = 0, limit = 50, tfn?: string): Promise<UnifiedTransaction[]> {
    try {
      // Backend should return joined data from transactions + pending_sales tables
      // SQL: SELECT t.*, ps.processing_status, ps.assigned_to_processor, ps.approved_by, ps.category, ps.sale_state
      //      FROM transactions t LEFT JOIN pending_sales ps ON t.id = ps.id
      const params = new URLSearchParams({ offset: String(offset), limit: String(limit) });
      if (tfn) params.set('tfn', tfn);
      const response = await api.get(`/transactions?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get transactions');
    }
  }

  // Server-side paginated + filtered page. Returns the page rows plus the
  // full-dataset totals (count + amount) computed with the SAME filters, so the
  // headline cards and the footer always agree. `filters` keys are backend param
  // names (search, created_by, bin, last4, date_from, ...).
  static async getTransactionsPaged(params: {
    offset?: number; limit?: number; sortField?: string; sortDir?: string;
    filters?: Record<string, string>;
  }): Promise<{ items: UnifiedTransaction[]; total: number; total_amount: number }> {
    try {
      const qp = new URLSearchParams();
      qp.set('offset', String(params.offset ?? 0));
      qp.set('limit', String(params.limit ?? 20));
      if (params.sortField) qp.set('sort_field', params.sortField);
      if (params.sortDir) qp.set('sort_dir', params.sortDir);
      Object.entries(params.filters || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && String(v).trim() !== '') qp.set(k, String(v));
      });
      const response = await api.get(`/transactions/paged?${qp.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get transactions');
    }
  }

  // Payment History filter dropdown choices across ALL pages (not just the loaded one)
  static async getFilterOptions(): Promise<TransactionFilterOptions> {
    const response = await api.get('/transactions/filter-options');
    return response.data;
  }

  // Analytics feed (team lead and above) — EVERY transaction (all types/statuses).
  static async getAnalytics(params: {
    offset?: number; limit?: number; sortField?: string; sortDir?: string;
    filters?: Record<string, string>;
  }): Promise<{ items: UnifiedTransaction[]; total: number; total_amount: number }> {
    try {
      const qp = new URLSearchParams();
      qp.set('offset', String(params.offset ?? 0));
      qp.set('limit', String(params.limit ?? 20));
      if (params.sortField) qp.set('sort_field', params.sortField);
      if (params.sortDir) qp.set('sort_dir', params.sortDir);
      Object.entries(params.filters || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && String(v).trim() !== '') qp.set(k, String(v));
      });
      const response = await api.get(`/transactions/analytics?${qp.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get analytics');
    }
  }

  // Download the COMPLETE filtered analytics dataset as a CSV file. Goes through
  // axios (blob) so the JWT auth header is sent, then triggers a browser download.
  static async exportAnalyticsCsv(filters: Record<string, string>, sort?: { field?: string; dir?: string }): Promise<void> {
    const qp = new URLSearchParams();
    if (sort?.field) qp.set('sort_field', sort.field);
    if (sort?.dir) qp.set('sort_dir', sort.dir);
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v).trim() !== '') qp.set(k, String(v));
    });
    const response = await api.get(`/transactions/analytics/export?${qp.toString()}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'analytics_export.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  static async getMyTransactions(offset = 0, limit = 50): Promise<UnifiedTransaction[]> {
    try {
      // Backend should return joined data for user's transactions
      const response = await api.get(`/transactions/my?offset=${offset}&limit=${limit}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get my transactions');
    }
  }

  static async decryptTransactionData(transactionId: string, reason: string): Promise<any> {
    try {
      const response = await api.post(`/secure-cards/transaction/${transactionId}/decrypt`, {
        reason: reason
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to decrypt transaction data');
    }
  }

  static async decryptCardData(transactionId: string, reason: string): Promise<any> {
    try {
      const response = await api.post(`/secure-cards/transaction/${transactionId}/decrypt`, {
        reason: reason
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to decrypt card data');
    }
  }

  static async getTransactionEligibility(transactionId: string): Promise<any> {
    try {
      const response = await api.get(`/transactions/${transactionId}/eligibility`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to check transaction eligibility');
    }
  }

  // Edit an existing sale's non-card details (admin/director/floor_manager).
  // recordId may be a transaction, pending-sale, or payment-link id.
  static async updateSaleDetails(recordId: string, data: Record<string, any>): Promise<any> {
    try {
      const response = await api.patch(`/transactions/${recordId}/details`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to save changes');
    }
  }

  static async voidTransaction(transactionId: string, reason: string, confirmation: boolean): Promise<any> {
    try {
      const response = await api.post(`/transactions/${transactionId}/void`, {
        reason,
        confirmation
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to void transaction');
    }
  }

  static async refundTransaction(transactionId: string, reason: string, confirmation: boolean): Promise<any> {
    try {
      const response = await api.post(`/transactions/${transactionId}/refund`, {
        reason,
        confirmation
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to refund transaction');
    }
  }
}

// Sales credit-transfer API (floor manager and above)
export interface TransferableAgent {
  id: string;
  full_name: string;
  username: string;
}

export interface SaleCreditTransferRecord {
  id: string;
  from_user_id: string | null;
  from_user_name: string | null;
  to_user_id: string;
  to_user_name: string | null;
  transferred_by: string;
  transferred_by_name: string | null;
  reason: string | null;
  transferred_at: string;
}

export interface EditHistoryEntry {
  id: string;
  edited_by?: string | null;
  edited_by_name?: string | null;
  edited_at?: string | null;
  source?: string | null;                 // 'sale_edit' | 'processing_section' | null
  changes?: Record<string, { old: any; new: any }> | null;
  transaction_id?: string | null;
  pending_sale_id?: string | null;
  payment_link_id?: string | null;
}

export class SalesAPI {
  static async getTransferHistory(params: { transaction_id?: string; sale_id?: string }): Promise<SaleCreditTransferRecord[]> {
    try {
      const response = await api.get('/sales/transfers', { params });
      return response.data;
    } catch (error: any) {
      // Non-fatal: the details panel still renders without transfer history.
      return [];
    }
  }

  // Activity log for the details panel — every recorded edit touching this
  // record (id may be a Transaction / PendingSale / PaymentLink id). Non-fatal.
  static async getEditHistory(recordId: string): Promise<EditHistoryEntry[]> {
    try {
      const response = await api.get(`/sales/${recordId}/edit-history`);
      return response.data;
    } catch (error: any) {
      return [];
    }
  }

  static async getTransferableAgents(): Promise<TransferableAgent[]> {
    try {
      const response = await api.get('/sales/transferable-agents');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to load agents');
    }
  }

  static async transferCredit(data: {
    transaction_id?: string;
    sale_id?: string;
    to_user_id: string;
    reason?: string;
  }): Promise<{ success: boolean; sale_id: string; credited_to: string }> {
    try {
      const response = await api.post('/sales/transfer-credit', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to transfer sale');
    }
  }
}

// Processor API class
export class ProcessorAPI {
  static async getProcessors(): Promise<any[]> {
    try {
      const response = await api.get('/processors');
      // Backend returns { processors: [...] }, extract the array
      return response.data.processors || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get processors');
    }
  }

  static async getDbProcessors(): Promise<any[]> {
    try {
      const response = await api.get('/processors/db');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get DB processors');
    }
  }
}

// Admin API class - using correct admin endpoints
export class AdminAPI {
  static async getUsers(): Promise<any[]> {
    try {
      console.log('AdminAPI: Calling /admin/users endpoint');
      const response = await api.get('/admin/users');
      console.log('AdminAPI: Users response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('AdminAPI: Error getting users:', error.response?.data);
      throw new Error(error.response?.data?.detail || 'Failed to get users');
    }
  }

  static async deleteUser(userId: string): Promise<void> {
    try {
      await api.delete(`/auth/users/${userId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to delete user');
    }
  }

  static async resetUserPassword(userId: string, newPassword?: string): Promise<any> {
    try {
      const response = await api.post(`/auth/admin/reset-user-password`, { 
        user_id: userId,
        new_password: newPassword 
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to reset user password');
    }
  }

  static async getPasswordResettableRoles(): Promise<string[]> {
    try {
      const response = await api.get('/auth/admin/password-resettable-roles');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get resettable roles');
    }
  }

  static async getUserPasswordHistory(userId: string): Promise<any[]> {
    try {
      const response = await api.get(`/auth/admin/password-history/${userId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get password history');
    }
  }
}

// Assignment API for processor assignment
// Main dashboard (GET /dashboard/summary). The server scopes it by role and only
// sends the sections that role may see.
export type DashboardPeriod = 'today' | 'yesterday' | 'week' | 'last_week' | 'month' | 'last_month' | 'custom';
export interface DashboardTotals {
  sales: number;
  amount: number;
  voids: number;
  void_amount: number;
  refunds: number;
  refund_amount: number;
  net: number;
}
export interface DashboardSalesRow extends DashboardTotals { name: string; }
export interface DashboardCountRow { name: string; sales: number; amount: number; }

export interface DashboardSummary {
  today: string;
  show_sales: boolean;
  show_agents: boolean;
  show_processors: boolean;
  show_work: boolean;
  show_approvals: boolean;
  range: { from: string; to: string };
  totals?: DashboardTotals;
  previous?: { from: string; to: string; net: number } | null;
  daily?: (DashboardTotals & { day: string })[];
  agents?: DashboardSalesRow[];
  processors?: DashboardSalesRow[];
  by_hour?: { hour: number; sales: number; amount: number }[];
  by_state?: DashboardCountRow[];
  by_domain?: DashboardCountRow[];
  by_tfn?: DashboardCountRow[];
  workload?: {
    scope: 'all' | 'team' | 'own';
    by_status: { status: string; count: number; amount: number }[];
    deposits: number;
    processors: { name: string; statuses: Record<string, number>; deposits: number; total: number }[];
  };
  approvals?: { sales: number; sales_amount: number; links: number; links_amount: number };
}

export class DashboardAPI {
  static async getSummary(period: DashboardPeriod, dateFrom?: string, dateTo?: string): Promise<DashboardSummary> {
    try {
      const params: Record<string, string> = { period };
      if (period === 'custom') {
        params.date_from = dateFrom || '';
        params.date_to = dateTo || '';
      }
      const response = await api.get('/dashboard/summary', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to load the dashboard');
    }
  }
}

export class AssignmentAPI {
  static async getPendingAssignments(offset: number = 0, limit: number = 100, highlight?: string): Promise<UnifiedTransaction[]> {
    try {
      // Get ALL assignments and filter for pending ones on frontend
      // This ensures Pending Processing page shows both pending and assigned for workload calculations
      const highlightParam = highlight ? `&highlight=${encodeURIComponent(highlight)}` : '';
      const response = await api.get(`/sales/assignments/all?offset=${offset}&limit=${limit}${highlightParam}`);
      
      // Filter for pending assignments only (for the Pending Processing table)
      const pendingOnly = response.data.filter((t: UnifiedTransaction) => 
        !t.processing_status || t.processing_status === 'PENDING_ASSIGNMENT'
      );
      
      return pendingOnly;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get pending assignments');
    }
  }

  static async getAllAssignments(offset: number = 0, limit: number = 100): Promise<UnifiedTransaction[]> {
    try {
      // Get ALL assignments (pending + assigned + processing) for workload calculations
      const response = await api.get(`/sales/assignments/all?offset=${offset}&limit=${limit}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get all assignments');
    }
  }

  // Server-side paginated + filtered pending assignments (Pending Processing table).
  static async getPendingAssignmentsPaged(
    offset: number,
    limit: number,
    filters: Record<string, string> = {},
    sortField: string = 'created_at',
    sortDir: string = 'desc',
  ): Promise<{ items: UnifiedTransaction[]; total: number; total_amount: number }> {
    try {
      const qp = new URLSearchParams({ offset: String(offset), limit: String(limit), sort_field: sortField, sort_dir: sortDir });
      Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qp.append(k, v); });
      const response = await api.get(`/sales/assignments/pending/paged?${qp.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get pending assignments');
    }
  }

  // Per-processor active-assignment counts (for the workload display).
  static async getWorkloadCounts(): Promise<Record<string, number>> {
    try {
      const response = await api.get('/sales/assignments/workload-counts');
      return response.data || {};
    } catch (error: any) {
      return {};
    }
  }

  static async getProcessingAssignments(offset: number = 0, limit: number = 100): Promise<UnifiedTransaction[]> {
    try {
      // Get ALL assignments from the pending assignments endpoint - this includes both pending and assigned
      // This endpoint returns the properly joined data from PendingSale + Transaction tables
      const response = await api.get(`/sales/assignments/pending?offset=${offset}&limit=200`);
      
      
      // Filter for processing assignments (ASSIGNED, IN_PROCESS, COMPLETED - not PENDING_ASSIGNMENT)
      const filtered = response.data.filter((t: UnifiedTransaction) => 
        t.processing_status && ['ASSIGNED', 'IN_PROCESS', 'COMPLETED'].includes(t.processing_status)
      );
      
      
      return filtered.slice(0, limit); // Limit to requested amount
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get processing assignments');
    }
  }

  static async getProcessingTransactions(offset: number = 0, limit: number = 100): Promise<UnifiedTransaction[]> {
    try {
      // processing_only: the server skips the unassigned backlog, otherwise the first
      // page is all PENDING_ASSIGNMENT and the filter below leaves the table empty.
      const response = await api.get(`/sales/assignments/all?offset=${offset}&limit=${limit}&processing_only=true`);

      // Filter for processing assignments (all statuses except PENDING_ASSIGNMENT)
      const filtered = response.data.filter((t: UnifiedTransaction) => 
        t.processing_status && t.processing_status !== 'PENDING_ASSIGNMENT'
      );
      
      return filtered;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get processing transactions');
    }
  }

  // Deposits page: sales marked as Deposit (server scopes: agent = own, TL = team, FM+ = all)
  static async getDepositSales(offset: number = 0, limit: number = 2000): Promise<UnifiedTransaction[]> {
    try {
      const response = await api.get(`/sales/assignments/deposits?offset=${offset}&limit=${limit}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get deposit sales');
    }
  }

  static async assignSaleToProcessor(saleId: string, processorUsername: string): Promise<any> {
    try {
      const response = await api.post(`/sales/assignments/${saleId}/assign-processor?processor_username=${processorUsername}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to assign sale to processor');
    }
  }


  static async getProcessorWorkload(processorUsername?: string): Promise<any> {
    try {
      const url = processorUsername ? `/processors/workload/${processorUsername}` : '/processors/workload';
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get processor workload');
    }
  }

  static async unassignSale(saleId: string): Promise<any> {
    try {
      const response = await api.post(`/sales/${saleId}/unassign-processor`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to unassign sale');
    }
  }

  static async getAvailableProcessorAgents(): Promise<any[]> {
    try {
      const response = await api.get('/processor-agents/available-agents');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get available processor agents');
    }
  }

  static async getMyAssignedSales(offset: number = 0, limit: number = 100): Promise<UnifiedTransaction[]> {
    try {
      const response = await api.get(`/processor-agents/my-assignments?offset=${offset}&limit=${limit}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get assigned sales');
    }
  }

  // Server-computed summary tiles over the FULL dataset (not the fetched page).
  static async getMyAssignedStats(): Promise<{ total_assigned: number; assigned_today: number; in_process: number; completed: number }> {
    try {
      const response = await api.get('/processor-agents/my-assignments/stats');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get assigned stats');
    }
  }

  // Headline counts for the Pending Processing page over the FULL dataset.
  static async getProcessingOverviewStats(): Promise<{ pending_assignment: number; total_workload: number }> {
    try {
      const response = await api.get('/sales/assignments/stats');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get processing stats');
    }
  }

  // Summary tiles for the global Processing page over the FULL dataset.
  static async getProcessingSummaryStats(): Promise<{ total_assigned: number; assigned_today: number; in_process: number; completed: number }> {
    try {
      const response = await api.get('/sales/assignments/processing-stats');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get processing summary stats');
    }
  }

  static async updateProcessingStatus(saleId: string, newStatus: string): Promise<any> {
    try {
      const response = await api.post(`/sales/assignments/${saleId}/update-status?new_status=${newStatus}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to update processing status');
    }
  }

  // Legacy method for backward compatibility
  static async assignTransaction(transactionId: string, assignedTo: string): Promise<any> {
    return this.assignSaleToProcessor(transactionId, assignedTo);
  }
}

// Settings API
export class SettingsAPI {
  static async getProcessorSettings(): Promise<any> {
    try {
      const response = await api.get('/settings/processor');
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return {
          testModeEnabled: false,
          threeDsEnabled: true,
          useQuickclick: false,
          updatedAt: new Date().toISOString(),
          updatedBy: 'system'
        };
      }
      throw new Error(error.response?.data?.detail || 'Failed to get processor settings');
    }
  }

  static async updateProcessorSettings(settings: any): Promise<any> {
    try {
      const response = await api.put('/settings/processor', settings);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to update processor settings');
    }
  }

  // Provider Management APIs
  static async getProviders(params?: { domain?: string; is_active?: boolean; offset?: number; limit?: number }): Promise<any> {
    try {
      const response = await api.get('/settings/providers', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get providers');
    }
  }

  static async getProvidersByDomain(domain: string): Promise<any[]> {
    try {
      const response = await api.get(`/settings/providers/by-domain/${domain}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get providers for domain');
    }
  }

  static async createProvider(provider: { name: string; domain: string; description?: string }): Promise<any> {
    try {
      const response = await api.post('/settings/providers', provider);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to create provider');
    }
  }

  static async updateProvider(providerId: string, updates: { name?: string; description?: string; is_active?: boolean }): Promise<any> {
    try {
      const response = await api.put(`/settings/providers/${providerId}`, updates);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to update provider');
    }
  }

  static async deleteProvider(providerId: string): Promise<void> {
    try {
      await api.delete(`/settings/providers/${providerId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to delete provider');
    }
  }

  static async getProvider(providerId: string): Promise<any> {
    try {
      const response = await api.get(`/settings/providers/${providerId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get provider');
    }
  }

  // Provider logo (PNG/JPG/WebP, max 2 MB). Upload/remove: provider-management roles.
  static async uploadProviderLogo(providerId: string, file: File): Promise<any> {
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await api.post(`/settings/providers/${providerId}/logo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to upload logo');
    }
  }

  static async removeProviderLogo(providerId: string): Promise<any> {
    try {
      const response = await api.delete(`/settings/providers/${providerId}/logo`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to remove logo');
    }
  }

  // Logo bytes as a Blob (the endpoint needs the auth header, so <img src> can't hit it directly).
  static async getProviderLogoBlob(providerId: string): Promise<Blob | null> {
    try {
      const response = await api.get(`/settings/providers/${providerId}/logo`, { responseType: 'blob' });
      return response.data;
    } catch {
      return null;
    }
  }

  // ===== TFN (Toll-Free Number) APIs =====
  // Read: any authenticated user (dropdown). Write: settings-access roles only.
  static async getTFNs(isActive?: boolean): Promise<TFNListResponse> {
    try {
      const params = new URLSearchParams();
      if (isActive !== undefined) params.set('is_active', String(isActive));
      const qs = params.toString();
      const response = await api.get(`/settings/tfns${qs ? `?${qs}` : ''}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get TFNs');
    }
  }

  static async createTFN(tfn: { number: string; label?: string }): Promise<TFN> {
    try {
      const response = await api.post('/settings/tfns', tfn);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to create TFN');
    }
  }

  static async updateTFN(tfnId: string, updates: { number?: string; label?: string; is_active?: boolean }): Promise<TFN> {
    try {
      const response = await api.put(`/settings/tfns/${tfnId}`, updates);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to update TFN');
    }
  }

  static async deleteTFN(tfnId: string): Promise<void> {
    try {
      await api.delete(`/settings/tfns/${tfnId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to delete TFN');
    }
  }

  // Agent Processor Routing APIs
  static async getAgentRoutings(): Promise<any> {
    try {
      const response = await api.get('/settings/agent-routing');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get agent routings');
    }
  }

  static async getAgentRouting(agentId: string): Promise<any> {
    try {
      const response = await api.get(`/settings/agent-routing/${agentId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get agent routing');
    }
  }

  static async updateAgentRouting(agentId: string, data: any): Promise<any> {
    try {
      const response = await api.put(`/settings/agent-routing/${agentId}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to update agent routing');
    }
  }

  static async updateAgentQuickclick(agentId: string, account: string | null): Promise<any> {
    try {
      const response = await api.put(`/settings/agent-routing/${agentId}/quickclick`, { account });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to update the processor');
    }
  }

  static async getMyRouting(): Promise<any> {
    try {
      const response = await api.get('/settings/my-routing');
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return { visa: null, visaName: null, mastercard: null, mastercardName: null, other: null, otherName: null };
      }
      throw new Error(error.response?.data?.detail || 'Failed to get my routing');
    }
  }
}

// Transaction Comments API
export class TransactionCommentsAPI {
  static async getComments(recordId: string, scope: string = 'general'): Promise<TransactionCommentsResponse> {
    try {
      const response = await api.get(`/comments/${recordId}`, { params: { scope } });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get comments');
    }
  }

  static async createComment(recordId: string, content: string, parentCommentId?: string, scope: string = 'general'): Promise<TransactionComment> {
    try {
      const response = await api.post(`/comments/${recordId}`, {
        content,
        parent_comment_id: parentCommentId
      }, { params: { scope } });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to create comment');
    }
  }

  static async deleteComment(recordId: string, commentId: string): Promise<void> {
    try {
      await api.delete(`/comments/${recordId}/${commentId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to delete comment');
    }
  }

  static async getMentionableUsers(search?: string): Promise<MentionableUser[]> {
    try {
      const response = await api.get('/comments/users/mentionable', {
        params: search ? { search } : undefined
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get mentionable users');
    }
  }
}

// Mentionable User type
export interface MentionableUser {
  id: string;
  username: string;
  full_name: string;
  role?: string;
}

// Transaction Documents API
export class TransactionDocumentsAPI {
  static async getDocuments(recordId: string): Promise<TransactionDocumentsResponse> {
    try {
      const response = await api.get(`/documents/${recordId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get documents');
    }
  }

  static async uploadDocument(recordId: string, file: File, description?: string): Promise<TransactionDocument> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (description) {
        formData.append('description', description);
      }

      const response = await api.post(`/documents/${recordId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to upload document');
    }
  }

  static async deleteDocument(recordId: string, documentId: string): Promise<void> {
    try {
      await api.delete(`/documents/${recordId}/${documentId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to delete document');
    }
  }

  static async getDocumentCount(recordId: string): Promise<DocumentCountResponse> {
    try {
      const response = await api.get(`/documents/${recordId}/count`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get document count');
    }
  }

  static getPreviewUrl(recordId: string, documentId: string): string {
    return `${API_BASE_URL}/documents/${recordId}/${documentId}/preview`;
  }

  static getDownloadUrl(recordId: string, documentId: string): string {
    return `${API_BASE_URL}/documents/${recordId}/${documentId}/download`;
  }
}

// Notifications API (FCM token management)
export class NotificationsAPI {
  static async registerFCMToken(token: string, deviceInfo?: string): Promise<{ status: string }> {
    try {
      const response = await api.post('/notifications/fcm/register', {
        token,
        device_info: deviceInfo
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to register FCM token');
    }
  }

  static async unregisterFCMToken(token: string): Promise<{ status: string }> {
    try {
      const response = await api.delete('/notifications/fcm/unregister', {
        data: { token }
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to unregister FCM token');
    }
  }

  static async getNotifications(limit: number = 50, offset: number = 0, unreadOnly: boolean = false): Promise<any[]> {
    try {
      const response = await api.get('/notifications', {
        params: { limit, offset, unread_only: unreadOnly }
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get notifications');
    }
  }

  static async getUnreadCount(): Promise<{ unread_count: number }> {
    try {
      const response = await api.get('/notifications/unread-count');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get unread count');
    }
  }

  static async markAsRead(notificationId: string): Promise<{ success: boolean }> {
    try {
      const response = await api.post(`/notifications/${notificationId}/mark-read`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to mark notification as read');
    }
  }

  static async markAllAsRead(): Promise<{ success: boolean; marked_read: number }> {
    try {
      const response = await api.post('/notifications/mark-all-read');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to mark all notifications as read');
    }
  }
}

// ===== 3DS API (ActiveServer Browser SDK proxy) =====

export class ThreeDSAPI {
  /**
   * Step 1: Initialize 3DS authentication.
   * Returns threeDSServerTransID, threeDSServerCallbackUrl, authUrl, etc.
   */
  static async brwInit(data: {
    acctNumber: string;
    cardExpiryDate?: string;
    purchaseAmount: string;
    purchaseCurrency?: string;
    merchantId?: string;
    challengeWindowSize?: string;
    payment_link_token?: string;
  }): Promise<any> {
    try {
      const response = await api.post('/3ds/brw/init', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || '3DS initialization failed');
    }
  }

  /**
   * Step 2: Execute 3DS authentication after browser info collection.
   * Returns transStatus, and if challenge: challengeUrl + base64EncodedCReq.
   */
  static async brwAuth(data: {
    threeDSServerTransID: string;
    browserInfo?: Record<string, any>;
  }): Promise<any> {
    try {
      const response = await api.post('/3ds/brw/auth', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || '3DS authentication failed');
    }
  }

  /**
   * Step 3: Get 3DS authentication result after challenge completion.
   * Returns transStatus, authenticationValue (CAVV), eci, etc.
   */
  static async brwResult(threeDSServerTransID: string): Promise<any> {
    try {
      const response = await api.get('/3ds/brw/result', {
        params: { threeDSServerTransID },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || '3DS result retrieval failed');
    }
  }
}

// ===== Payment Link API =====

export class PaymentLinkAPI {
  // Processors a QuickClick link can go through, default first.
  static async getQuickclickAccounts(): Promise<{ account: string; name: string }[]> {
    const response = await api.get('/payment-links/quickclick-accounts');
    return response.data?.accounts || [];
  }

  static async create(data: CreatePaymentLinkData): Promise<PaymentLinkData> {
    try {
      const response = await api.post('/payment-links', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to create payment link');
    }
  }

  static async list(
    skip: number = 0,
    limit: number = 50,
    filters?: {
      status?: string;
      sale_state?: string;
      three_ds?: 'YES' | 'NO';
      date_field?: 'created' | 'paid';
      date_from?: string;
      date_to?: string;
    }
  ): Promise<PaymentLinkListResponse> {
    try {
      const params: Record<string, string | number> = { skip, limit };
      if (filters) {
        Object.entries(filters).forEach(([k, v]) => {
          if (v !== undefined && v !== null && String(v).trim() !== '' && v !== 'ALL') {
            params[k] = v as string;
          }
        });
      }
      const response = await api.get('/payment-links', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to list payment links');
    }
  }

  // Server-computed counts/totals over the FULL dataset (not the fetched page).
  static async getStats(): Promise<{ total: number; by_status: Record<string, number>; total_amount: number; completed_amount: number; sale_states: string[] }> {
    try {
      const response = await api.get('/payment-links/stats');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get payment link stats');
    }
  }

  static async getById(linkId: string): Promise<PaymentLinkData> {
    try {
      const response = await api.get(`/payment-links/${linkId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get payment link');
    }
  }

  // Full charge-attempt history for a link (approved + declined, with reasons).
  static async getTransactions(linkId: string): Promise<{
    link_id: string; total: number; approved: number; failed: number;
    attempts: Array<{ id: string; created_at: string; amount: number; tx_type: string; status: string; response_code: string | null; reason: string | null; nmi_transactionid: string | null }>;
  }> {
    try {
      const response = await api.get(`/payment-links/${linkId}/transactions`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get attempt history');
    }
  }

  static async cancel(linkId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post(`/payment-links/${linkId}/cancel`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to cancel payment link');
    }
  }

  static async resend(linkId: string, method: "email" | "sms" = "email"): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post(`/payment-links/${linkId}/resend`, null, {
        params: { method },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to resend payment link');
    }
  }

  // ===== Approval workflow (supervisor) =====
  static async getPendingApproval(skip: number = 0, limit: number = 50): Promise<PendingPaymentLinkListResponse> {
    try {
      const response = await api.get('/payment-links/pending-approval', { params: { skip, limit } });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to load pending payment links');
    }
  }

  static async approve(
    linkId: string,
    data: {
      three_ds_required: boolean;
      delivery_method: "email" | "sms" | "both";
      // Per-category processor overrides. null/undefined = inherit agent routing.
      quickclick_account?: string | null;
      processor_visa_id?: string | null;
      processor_mastercard_id?: string | null;
      processor_other_id?: string | null;
    }
  ): Promise<PaymentLinkData> {
    try {
      const response = await api.post(`/payment-links/${linkId}/approve`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to approve payment link');
    }
  }

  static async reject(linkId: string, reason?: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post(`/payment-links/${linkId}/reject`, { reason });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to reject payment link');
    }
  }

  // Public endpoints (no auth required)
  static async getForCustomer(token: string): Promise<PaymentLinkCustomerView> {
    try {
      const response = await api.get(`/payment-links/pay/${token}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Payment link not found or expired');
    }
  }

  static async customerPay(token: string, cardData: {
    ccnumber: string;
    ccexp: string;
    cvv: string;
    three_ds_cavv?: string;
    three_ds_eci?: string;
    three_ds_xid?: string;
    three_ds_version?: string;
    three_ds_status?: string;
    three_ds_server_trans_id?: string;
  }): Promise<CustomerPayResponse> {
    try {
      const response = await api.post(`/payment-links/pay/${token}`, cardData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Payment processing failed');
    }
  }

  static async complete3ds(token: string, data: { server_trans_id: string; ccnumber: string; ccexp: string; cvv: string }): Promise<CustomerPayResponse> {
    try {
      const response = await api.post(`/payment-links/pay/${token}/3ds-complete`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Payment processing failed after 3DS');
    }
  }
}

// ---- Customer document-upload request links --------------------------------
export class DocumentRequestAPI {
  // Staff: active link + history for a sale's panel
  static async get(recordId: string): Promise<any> {
    try {
      const response = await api.get(`/document-requests/${recordId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to load document request');
    }
  }
  static async create(recordId: string, deliveryMethod: 'email' | 'sms' | 'both', provider?: string): Promise<any> {
    try {
      const response = await api.post(`/document-requests/${recordId}`, { delivery_method: deliveryMethod, provider });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to send document request');
    }
  }
  static async resend(recordId: string, deliveryMethod: 'email' | 'sms' | 'both'): Promise<any> {
    try {
      const response = await api.post(`/document-requests/${recordId}/resend`, { delivery_method: deliveryMethod });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to resend document request');
    }
  }
  static async cancel(recordId: string): Promise<any> {
    try {
      const response = await api.post(`/document-requests/${recordId}/cancel`, {});
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to cancel document request');
    }
  }
  // Public (customer) upload page
  static async getUploadPage(token: string): Promise<any> {
    try {
      const response = await api.get(`/document-requests/upload/${token}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'This document link is not available.');
    }
  }
  static async submit(token: string, formData: FormData): Promise<any> {
    try {
      const response = await api.post(`/document-requests/upload/${token}/submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to submit documents.');
    }
  }
}

export default api;
